import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthService } from '../lib/services/AuthService';
import { AlertCircle, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import Logo from '../logo.svg';
import { logAccess, AccessType } from '../lib/services/AccessLoggerService';

const VerifyLoginCode: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    // Récupérer l'email depuis l'état de navigation ou sessionStorage
    const emailFromState = location.state?.email;
    const emailFromStorage = sessionStorage.getItem('pending_login_email');
    const emailToUse = emailFromState || emailFromStorage || '';
    
    if (!emailToUse) {
      // Si pas d'email, rediriger vers la page de connexion
      navigate('/login');
      return;
    }
    
    setEmail(emailToUse);
  }, [location, navigate]);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!code || code.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres reçu par email');
      return;
    }

    // Récupérer le mot de passe depuis sessionStorage
    const password = sessionStorage.getItem('pending_login_password');
    if (!password) {
      setError('Session expirée. Veuillez vous reconnecter.');
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const { error: verifyError } = await AuthService.verifyLoginCode(email, code, password);
      
      if (verifyError) {
        setError(verifyError);
        return;
      }

      // Marquer ce navigateur comme connu après vérification réussie
      AuthService.markBrowserAsKnown(email);

      // Nettoyer sessionStorage
      sessionStorage.removeItem('pending_login_email');
      sessionStorage.removeItem('pending_login_password');

      // Enregistrer la connexion dans l'historique (après vérification du code)
      logAccess(AccessType.LOGIN, `Connexion réussie avec vérification par code: ${email}`).catch(console.error);

      setSuccessMsg('Code vérifié avec succès. Connexion en cours...');
      
      // Rediriger vers la page d'accueil après un court délai
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccessMsg(null);
    
    try {
      setLoading(true);
      const { error: sendError } = await AuthService.sendLoginVerificationCode(email);
      
      if (sendError) {
        setError(sendError);
        return;
      }

      setSuccessMsg('Code renvoyé par email. Vérifiez votre boîte de réception.');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Nettoyer sessionStorage
    sessionStorage.removeItem('pending_login_email');
    sessionStorage.removeItem('pending_login_password');
    navigate('/login');
  };

  if (!email) {
    return null; // En attente de la redirection
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src={Logo} alt="Logo" className="w-20 h-20" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Vérification de sécurité
          </h2>
          <p className="text-dark-400">
            Vous tentez de vous connecter depuis un nouveau navigateur ou appareil.
            <br />
            Veuillez entrer le code de vérification envoyé à <strong className="text-white">{email}</strong>
          </p>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 shadow-xl">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-green-400 text-sm">{successMsg}</span>
            </div>
          )}

          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-dark-400 mb-2">
                Code de vérification
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-2xl tracking-widest"
                placeholder="000000"
                disabled={loading}
                maxLength={6}
                autoFocus
              />
              <p className="mt-2 text-xs text-dark-500 text-center">
                Entrez le code à 6 chiffres reçu par email
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleBackToLogin}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour</span>
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Vérification...</span>
                  </>
                ) : (
                  <span>Vérifier</span>
                )}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-50"
            >
              Renvoyer le code
            </button>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyLoginCode;

